import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import styled, { css } from 'styled-components/macro';
import { useIntl } from 'react-intl';
import { Pagination, Panel, Panels, Tab, Tabs } from '@tovia/man-ui';
import { destructUUID } from '@tovia/man-app-utils/lib/helpers/uuids';
import { GalleryPlaceholder } from 'src/client/components/Placeholders/Layouts/GalleryPlaceholder';
import PaginationModal from 'src/client/components/modals/PaginationModal';
import RecentCookiesItemInjector from '../RecentCookies/RecentCookiesItemInjector';
import {
  GalleryPhotoMedia,
  load as loadFn,
  ModelGalery,
  reset as resetGalleryFn,
} from 'src/client/redux/modules/gallery';
import { reset as resetPermissionsFn } from 'src/client/redux/modules/galleryPermissions';
import { FULL_MODE, PPS_PURCHASED, REGULAR_GUEST_MODE } from 'src/client/helpers/constants';
import GalleryNavigationLink from './GalleryNavigationLink';
import CamsSlider from '../Cams/CamsSlider/CamsSlider';
import { CommentSection } from '../CommentSection/CommentSection';
import { GalleryPanels } from '../Panels/GalleryPanels';
import { ModelPanels } from '../Panels/ModelPanels';
import PhotographerPanels from '../Panels/PhotographerPanels';
import { galleryImageUrl } from 'src/client/helpers/urls';
import { useSelector, useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import Helmet from '../Helmet/Helmet';
import urls from 'src/shared/urls';
import { Site } from 'src/client/redux/modules/site';
import { isPrerender } from 'src/client/utils/prerenderDetect';
import { DESKTOP, MOBILE_LANDSCAPE, TABLET_LANDSCAPE, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { JoinButton, useJoinUrl } from 'src/client/components/buttons/JoinButton';
import { ImageLink } from 'src/client/components/ui/ImageLink';
import { PromoBanner } from 'src/client/containers/PopupBanners/PromoBanner';
import useAccessToken from '../../helpers/useAccessToken';
import { PartnerInlineBanner } from 'src/client/components/PartnerBanners/PartnerInlineBanner';
import { PromoInlineBanner } from 'src/client/containers/Promo/PromoInlineBanner';
import { PromoCrossBanner } from 'src/client/containers/Promo/PromoCrossBanner';
import { usePromotions } from 'src/client/helpers/usePromotions';
import { GalleryResponseModel } from 'src/@types/app';
import { GalleryCategories } from '@tovia/man-protos/dist/types/content.types';
import { Gallery } from 'src/@types/Gallery';
import { NetworkGalleriesSlider } from 'src/client/components/NetworkGalleriesSlider';
import { maxBy } from 'lodash';

const ITEMS_PER_ROW = 6;
const DEFAULT_GALLERY_TABS = ['gallery', 'model', 'photographer'];
const DEFAULT_ACTIVE_TAB = 'gallery';

const sliderSettings = {
  autoplay: false,
  draggable: true,
  responsive: [
    {
      breakpoint: 986,
      settings: { slidesToShow: 4, slidesToScroll: 4 },
    },
    {
      breakpoint: 1200,
      settings: { slidesToShow: 5, slidesToScroll: 5 },
    },
    {
      breakpoint: 100000,
      settings: { slidesToShow: 6, slidesToScroll: 6 },
    },
  ],
};

const StyledPagination = styled(Pagination)`
  @media (max-width: 991px) {
    order: 3;
    flex-basis: 100%;
    width: 100%;
  }
`;

const NavigationLinkWrap = styled.div`
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 991px) {
    flex-wrap: wrap;
    .previous {
      order: 1;
    }

    .next {
      order: 2;
    }
  }
`;

const GalleryPhotosWrap = styled.div<{ fullThumbnails?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;

  ul {
    display: grid;
    column-gap: 15px;
    row-gap: 20px;
    margin: 0;
    width: 100%;
    position: relative;

    // grid template columns for each size, starting with mobile.
    grid-template-columns: repeat(2, 1fr);

    @media ${MOBILE_LANDSCAPE} {
      grid-template-columns: repeat(3, 1fr);
    }

    @media ${TABLET_PORTRAIT} {
      row-gap: 25px;
      grid-template-columns: repeat(4, 1fr);
    }

    @media ${TABLET_LANDSCAPE} {
      grid-template-columns: repeat(5, 1fr);
    }

    @media ${DESKTOP} {
      grid-template-columns: repeat(6, 1fr);
    }
  }

  img {
    width: 100%;
    ${(props) =>
      !props.fullThumbnails &&
      css`
        object-fit: cover;
        aspect-ratio: 1 / 1;
      `}
  }
`;

const ListItem = styled.li`
  && {
    padding: 0;
    overflow: hidden;
  }
`;

const Wrap = styled.div`
  padding-top: 17px;
  padding-bottom: 15px;
  background: ${(props) => props.theme.primary1};

  .panels-tabs {
    margin-bottom: 20px;
  }

  .description-text a,
  .panel-content p a {
    text-decoration: underline;
  }

  .man-ui.panels .panel-content {
    padding: 0;
  }
`;

const eventTrigger = (_name, _page) => {
  window.dataLayer.push({
    event: 'select_content',
    content_type: 'gallery',
    item_id: `${_name}`,
    page: _page || 1,
  });
};

interface Props {
  className?: string;
}

const GalleryContainer = (props: Props) => {
  const { className = '' } = props;
  const [changingPage, setChangingPage] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(DEFAULT_ACTIVE_TAB);
  const [accessMode, setAccessMode] = useState(REGULAR_GUEST_MODE);
  const [site, setSite] = useState<Site | null>();
  const parentSite = useSelector((state) => state.site);
  const { siteSubdomain } = useSelector((state) => state.app);
  const { user } = useSelector((state) => state.auth);
  const thumbnailsPerPage = useSettingsSelector('thumbnailsPerPage') as number;
  const fullThumbnails = useSettingsSelector('thumbnail') as number;
  const imageWindow = useSettingsSelector('imageWindow');
  const resolution = useSettingsSelector('resolution');
  const navigation = useSettingsSelector('navigation');
  const galleryPermissions = useSelector((state) => state.galleryPermissions);
  const sites = useSelector((state) => state.sites.items);
  const { pixelApiUrl, config, contentCdnUrl } = useSelector((state) => state.app);
  const {
    error,
    loaded,
    item,
    globalContent,
    item: {
      metaDescription,
      metaTitle,
      models: galleryModels,
      photographers: galleryPhotographers,
      name,
      path: galleryPath,
      siteUUID,
      UUID,
      photos: galleryPhotos,
      categories,
      isPromoGallery,
    },
  } = useSelector((state) => state.gallery);

  const [showPromoBanner, setShowPromoBanner] = useState(false);
  const [isPromoCrossBannerActive, setPromoCrossBannerActive] = useState(false);

  useEffect(() => {
    if (user && item && isPromoGallery && parentSite && item.UUID !== parentSite.UUID) {
      const isSubscribedToPromoGallerySite = user?.subscriptions?.some(
        (subscription) => destructUUID(subscription.siteUUID) === item.siteUUID,
      );

      setShowPromoBanner(!isSubscribedToPromoGallerySite);
      setPromoCrossBannerActive(!isSubscribedToPromoGallerySite);
    }
  }, [item, isPromoGallery, parentSite, user]);

  const dispatch = useDispatch();
  const load = useCallback((args) => dispatch(loadFn(args)), [dispatch]);
  const resetGallery = useCallback(() => dispatch(resetGalleryFn()), [dispatch]);
  const resetPermissions = useCallback(() => dispatch(resetPermissionsFn()), [dispatch]);

  const galleryParams = useParams<{ galleryName: string; date: string; page: string }>();
  const location = useLocation();
  const history = useHistory();

  const intl = useIntl();
  const [joinUrl] = useJoinUrl({ campaign: 'gallery-thumbnail' });
  const { isPromoInlineBannerActive } = usePromotions();

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history]);

  const isMember = user?.validSubscription;
  const { galleryName, date, page } = galleryParams;
  const { accessToken: token } = useAccessToken();
  const thumbnailCount = useMemo(() => ITEMS_PER_ROW * Math.round(thumbnailsPerPage / ITEMS_PER_ROW), [
    thumbnailsPerPage,
  ]);

  const loadData = useCallback(() => {
    const reqParams = {
      name: galleryName,
      thumbnailsPerPage: thumbnailCount,
      date,
    };

    // @ts-ignore
    reqParams.thumbnailPage = page ? parseInt(page) : 1;

    if (token) {
      // @ts-ignore
      reqParams.token = token;
    }

    load(reqParams);
  }, [load, galleryName, date, page, token, thumbnailCount]);

  useEffect(() => {
    loadData();
    setChangingPage(false);
  }, [loadData, galleryParams.page]);

  useEffect(() => {
    const isPPSPurchased = item && (item.hasPermissions || galleryPermissions.allowedStreamIds.length > 0);
    if (isMember) {
      setAccessMode(FULL_MODE);
    } else if (isPPSPurchased) {
      setAccessMode(PPS_PURCHASED);
    }
  }, [item, isMember, galleryPermissions]);

  // reset gallery on unmount
  useEffect(
    () => () => {
      resetGallery();
      resetPermissions();
    },
    [resetGallery, resetPermissions],
  );

  useEffect(() => {
    if (!isPrerender && !changingPage && item && galleryPermissions.loaded) {
      eventTrigger(item.name, page);
    }
  }, [changingPage, page, item, galleryPermissions]);

  useEffect(() => {
    if (siteUUID) {
      const found = sites.find((s) => s.UUID === siteUUID);
      if (found) {
        setSite(found);
      }
    }
  }, [sites, siteUUID]);

  const renderHelmetElement = () => {
    if (!site) {
      return null;
    }

    const dbTitle = metaTitle && !isMember ? metaTitle : null;
    let title = dbTitle;
    const modelsCount = galleryModels.length;

    const models = (galleryModels as ModelGalery[]).reduce((acc, currentItem, index) => {
      const { publishAge: debutAge, country, hair: hairColor } = currentItem;
      // let's do it starts from 1, for dummies seo guys
      acc[`model${index + 1}`] = {
        name: currentItem.name,
        debutAge,
        country: country?.name || '',
        hairColor,
      };
      return acc;
    }, {});

    const mostPopularModel = maxBy(galleryModels, ({ ratingAverage }) => ratingAverage);

    const photographers = galleryPhotographers.reduce((acc, currentItem, index) => {
      // let's do it starts from 1, for dummies seo guys
      acc[`photographer${index + 1}`] = {
        name: currentItem.name,
      };
      return acc;
    }, {});

    if (!title) {
      // in case title is not set on the db
      switch (modelsCount) {
        case 1:
          title = "{{model1.name}} in '{{{galleryName}}}' - {{siteName}}";
          break;
        case 2:
          title = "{{model1.name}} & {{model2.name}} in '{{{galleryName}}}' - {{siteName}}";
          break;
        case 3:
          title = "{{model1.name}}, {{model2.name}} & {{model3.name}} in '{{{galleryName}}}'- {{siteName}}";
          break;
        default:
          title = '{{{galleryName}}} by {{model1.name}}, {{model2.name}} and others - {{siteName}}';
          break;
      }
    }
    return (
      <Helmet
        id={`gallery-${modelsCount}`}
        canonicalUrl={`https://${siteSubdomain}.${site.domainCanonical}${mostPopularModel?.path}`}
        defaultTitle={title}
        defaultDescription={metaDescription}
        forceDefaultTitle={!!dbTitle}
        forceDefaultDescription={!!metaDescription}
        variables={{
          ...models,
          ...photographers,
          photoCount: galleryPhotos?.total,
          // galleryModels,
          // galleryPhotographers,
          galleryName: name,
          siteName: site.name,
        }}
      >
        <script>{`var isStaffSelectionGallery = 0;`}</script>
      </Helmet>
    );
  };

  const renderPixel = () => (
    <img src={`${pixelApiUrl}/${siteUUID}/GALLERY/${UUID}`} style={{ display: 'none' }} alt="pixel" />
  );

  const renderTabs = () => {
    const allowedTabs = config.photoPage.tabs || DEFAULT_GALLERY_TABS;
    const { hasPhotographers } = config;
    type Tab = { name?: string; value?: string; type?: string };
    const tabs: Tab[] = [
      {
        name: intl.formatMessage(
          { id: 'gallery.tabs.gallery', defaultMessage: `Gallery: ${name}` },
          { galleryName: name },
        ),
        value: 'gallery',
        type: 'gallery',
      },
      ...galleryModels.map((model) => ({
        name: intl.formatMessage(
          { id: 'gallery.tabs.cast', defaultMessage: `Cast: ${model.name}` },
          { modelName: model.name },
        ),
        value: `model_${model.UUID}`,
        type: 'model',
      })),
      ...(hasPhotographers
        ? galleryPhotographers.map((photographer) => ({
            name: intl.formatMessage(
              { id: 'gallery.tabs.photographer', defaultMessage: `Photographer: ${photographer.name}` },
              { photographerName: photographer.name },
            ),
            value: `photographer_${photographer.UUID}`,
            type: 'photographer',
          }))
        : [{} as Tab]),
    ].filter((tab) => allowedTabs.includes(tab.type));

    return (
      <>
        <Tabs flat className="panels-tabs">
          {tabs.map((tab) => (
            <React.Fragment key={tab.value}>
              <Tab {...tab} active={activeTab === tab.value} onClick={() => setActiveTab(tab.value ?? '')} />{' '}
            </React.Fragment>
          ))}
        </Tabs>
      </>
    );
  };

  const renderPanels = () => {
    const isGallery = activeTab === 'gallery';
    const panelModel = galleryModels.find((model) => activeTab === `model_${model.UUID}`) as GalleryResponseModel;
    const isModel = !!panelModel;
    const panelPhotographer = galleryPhotographers.find(
      (photographer) => activeTab === `photographer_${photographer.UUID}`,
    );
    const isPhotographer = !!panelPhotographer;
    const isCompilationSite = Boolean(config.compilation.params?.siteUUIDs);

    if (isGallery) {
      return <GalleryPanels gallery={item as Gallery} accessMode={accessMode} key={item.UUID} />;
    }

    if (isModel) {
      let modelPath = panelModel.path;
      if (site?.UUID !== parentSite?.UUID && !isCompilationSite) {
        modelPath = `https://${siteSubdomain}.${site?.domainCanonical}${panelModel.path}`;
      }
      return <ModelPanels model={{ ...panelModel, path: modelPath }} key={panelModel.UUID} />;
    }

    if (isPhotographer) {
      let photographerPath = panelPhotographer.path;
      if (site?.UUID !== parentSite?.UUID && !isCompilationSite) {
        photographerPath = `https://${siteSubdomain}.${site?.domainCanonical}${panelPhotographer.path}`;
      }
      return (
        <PhotographerPanels
          photographer={{ ...panelPhotographer, siteUUID, path: photographerPath }}
          key={panelPhotographer.UUID}
        />
      );
    }
    return null;
  };

  const renderGalleryNavigationLink = (direction) => {
    const { cleanGalleryCovers } = config;
    const object = item?.[`${direction}Gallery`];

    if (object) {
      return <GalleryNavigationLink item={object} direction={direction} cleanGalleryCover={cleanGalleryCovers} />;
    }
    return null;
  };

  const pageUrlPrefix = useMemo(() => `${galleryPath}`, [galleryPath]);
  const pageUrlSuffix = useMemo(() => `/${token ? `?token=${token}` : ''}`, [token]);

  const setPage = useCallback(
    (pageNumber) => {
      setChangingPage(true);
      history.push(`${pageUrlPrefix}/${pageNumber}${pageUrlSuffix}`);
    },
    [setChangingPage, history, pageUrlPrefix, pageUrlSuffix],
  );

  const renderPagination = () => {
    const { total } = galleryPhotos ?? { total: 0 };
    return (
      <StyledPagination
        activePage={parseInt(page) || 1}
        boundaryRange={1}
        onPageChange={setPage}
        siblingRange={1}
        totalPages={Math.ceil(total / thumbnailCount)}
        showPrevNext
        pageUrlPrefix={pageUrlPrefix}
        pageUrlSuffix={pageUrlSuffix}
        modal={
          <PaginationModal
            setPage={(pageNumber) => setPage(pageNumber)}
            totalPages={Math.ceil(total / thumbnailCount)}
          />
        }
      />
    );
  };

  const renderJoinMessage = () => (
    <div className="hidden-xs hidden-sm gallery-join">
      <JoinButton campaign="gallery-page">
        {intl.formatMessage(
          {
            id: 'gallery.accessFullSet',
            defaultMessage: `Access the full set of ${galleryPhotos?.total} photos by joining today!`,
          },
          { photosTotal: galleryPhotos?.total },
        )}
      </JoinButton>
    </div>
  );

  const renderGalleryNavigation = (isHeader = false) => {
    const previousLink = renderGalleryNavigationLink('previous');
    const nextLink = renderGalleryNavigationLink('next');
    const pagination = renderPagination();
    const join = renderJoinMessage();
    const hasAccess = accessMode === FULL_MODE || accessMode === PPS_PURCHASED;

    return (
      <NavigationLinkWrap>
        {previousLink}
        {hasAccess ? pagination : (isHeader && join) || null}
        {nextLink}
      </NavigationLinkWrap>
    );
  };

  const renderGalleryPhotoUrl = ({ order, index }) => {
    const hasAccess = accessMode === FULL_MODE || accessMode === PPS_PURCHASED;
    const isSimpleNavigation = navigation === 'simple';

    const imageViewerPhotoUrl = `${galleryPath}/image/${order}${token ? `?token=${token}` : ''}#galleryPage=${
      galleryParams.page || 1
    }`;

    const cndImageUrl = (galleryPhotos?.media[index] as GalleryPhotoMedia).imgPath?.[`${resolution}`];

    const imageUrl = isSimpleNavigation ? cndImageUrl : imageViewerPhotoUrl;
    return hasAccess ? imageUrl : joinUrl;
  };

  const renderGalleryThumbs = () => {
    const target = imageWindow === 'new' ? '_blank' : '_self';
    const rel = imageWindow === 'new' ? 'noopener noreferrer' : undefined;
    return (
      <GalleryPhotosWrap fullThumbnails={+fullThumbnails === 1}>
        <ul className="list-unstyled list-inline list">
          {galleryPhotos?.media.map((photo, index) => (
            <ListItem key={photo.UUID}>
              <ImageLink
                item={{
                  imgSrc: galleryImageUrl(UUID, photo.UUID, photo.siteUUID, contentCdnUrl),
                  url: renderGalleryPhotoUrl({ order: photo.displayOrder, index }),
                  altText: 'gallery-thumbnail',
                }}
                target={target}
                rel={rel}
                className="gallery-thumbnail"
                extraProps={{ prevPath: location.pathname, galleryPage: galleryParams.page }}
              />
            </ListItem>
          ))}
        </ul>
      </GalleryPhotosWrap>
    );
  };

  const renderCommentSection = () => (
    <CommentSection
      objectUUID={item?.UUID}
      parentItem={item}
      parentItemType="gallery"
      {...(isMember
        ? {}
        : {
            generateTitle: ({ commentCount }) =>
              `${commentCount} Member${commentCount === 1 ? '' : 's'} commented on ${galleryModels
                .map((model) => model.name)
                .join(' & ')}'s gallery`,
          })}
    />
  );

  const loaderMarkup = loaded ? null : <GalleryPlaceholder count={thumbnailsPerPage} />;
  const helmetMarkup = loaded ? renderHelmetElement() : null;
  const pixelMarkup = loaded ? renderPixel() : null;
  const tabsMarkup = loaded ? renderTabs() : null;
  const panelsMarkup = loaded ? renderPanels() : null;

  const thumbnailsHeaderMarkup = loaded ? renderGalleryNavigation(true) : null;
  const galleryThumbs = loaded ? renderGalleryThumbs() : null;
  const thumbnailsFooterMarkup = loaded ? renderGalleryNavigation() : null;
  const camsSliderMarkup =
    loaded && !!user && config.hasCamsSlider ? (
      <CamsSlider camTrackingPosition="slider" settings={sliderSettings} />
    ) : null;
  const commentSectionMarkup = loaded ? renderCommentSection() : null;
  const clearfix = <div className="clear" />;
  const inlinePromoBannerMarkup = isPromoInlineBannerActive ? <PromoInlineBanner /> : null;
  const crossPromoBannerMarkup =
    isPromoCrossBannerActive && site ? <PromoCrossBanner promoSite={site} promoGalleryUUID={item.UUID} /> : null;

  const galleryContentMarkup = loaded ? (
    <Panels>
      <Panel className="col-xs-12">
        {thumbnailsHeaderMarkup}
        {inlinePromoBannerMarkup}
        {crossPromoBannerMarkup}
        {!isPromoInlineBannerActive && !isPromoCrossBannerActive && (
          <PartnerInlineBanner
            setPromoPhotographer={galleryPhotographers?.[0]?.UUID}
            categories={categories as GalleryCategories[]}
            site={site}
            setType="gallery"
          />
        )}
        {galleryThumbs}
        {thumbnailsFooterMarkup}
      </Panel>
      {clearfix}
    </Panels>
  ) : null;

  const networkGallerySliderMarkup =
    loaded && globalContent?.length ? <NetworkGalleriesSlider galleries={globalContent} /> : null;

  const recentCookiesInjectorMarkup =
    loaded && item ? (
      <RecentCookiesItemInjector
        type={item.type}
        path={item.path}
        name={item.name}
        siteUUID={item.siteUUID}
        thumbnailCoverPath={item.thumbnailCoverPath}
      />
    ) : null;
  return (
    <Wrap className={`content container ${className}`}>
      {loaderMarkup}
      {helmetMarkup}
      {pixelMarkup}
      {tabsMarkup}
      {panelsMarkup}
      {galleryContentMarkup}
      {camsSliderMarkup}
      {commentSectionMarkup}
      {networkGallerySliderMarkup}
      {recentCookiesInjectorMarkup}
      {showPromoBanner && site && (
        <PromoBanner promoSite={site} promoGalleryUUID={item.UUID} onClose={() => setShowPromoBanner(false)} />
      )}
    </Wrap>
  );
};

export default GalleryContainer;
